import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Spacer from '@berlitz/spacer'
import Modal from '@berlitzplatforms/micro.ui.modal'
import ImageWelcome from '@berlitzplatforms/micro.image.welcome'
import Select from 'react-select'
import { useIntl, FormattedMessage } from 'react-intl'
import { Row, Col } from 'react-styled-flexboxgrid'
import isNumber from 'lodash/isNumber'
import WarningModal from '@components/Modal/WarningModal'
import BerlitzButton from '@berlitzplatforms/micro.ui.button'
import { H1 as BerlitzH1 } from '@berlitz/globals'
import SelectedItem from './SelectedItem'

// ==================== TYPES ====================

interface ProductOption {
  label: string
  value: string
  key: string
  selected?: boolean
  level: string
  index?: number
  isDisabled?: boolean
  slub?: string
}

export interface ModalSelectSkillProductProps {
  id?: string
  sample?: boolean
  open?: boolean
  onClose: () => void
  onButtonClick?: (electives: ProductOption[]) => void
  onSelected?: (selected?: string) => void
  size?: 'sm' | 'md' | 'lg'
  title: React.ReactNode
  description?: React.ReactNode
  buttonText?: React.ReactNode
  options?: ProductOption[]
  selectLabel?: string
  maxLimit?: number
  initialSelected?: string[]
  remainingSwitches?: number
  initialPlaceHolders?: string[]
}

interface LabelProp {
  warning?: boolean
  error?: boolean
}

// ==================== STYLES ====================

const StyledMultiSelect = styled.div`
  ${({ theme }) => `
    width: 100%;

    input[type='text'] {
      ::-webkit-input-placeholder {
        color: ${theme.palette.charcoal};
      }
      ::-moz-placeholder {
        color: ${theme.palette.charcoal};
      }
      :-ms-input-placeholder {
        color: ${theme.palette.charcoal};
      }
      :-moz-placeholder {
        color: ${theme.palette.charcoal};
      }
    }
  `}
`

const StyledModalSelectSkillsTutor = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${({ theme }) => ` .ant-select-dropdown {
    border: solid 1px ${theme.palette.gray100};
    box-shadow: none;
  }

  .ant-select-dropdown-menu-item {
    color: ${theme.colors.brandSecondaryContrast};
  }

  .ant-select-dropdown-menu-item-selected {
    color: ${theme.colors.brandPrimary};
    font-weight: normal;
  }

  .ant-select-dropdown-menu-item:hover,
  .ant-select-dropdown-menu-item-active,
  .ant-select-dropdown-menu-item-active:not(.ant-select-dropdown-menu-item-disabled),
  .ant-select-dropdown-menu-item:hover:not(.ant-select-dropdown-menu-item-disabled){
    background-color: ${theme.colors.brandPrimary};
    color: white;
    font-weight: bold;
  }

  .ant-select-dropdown-menu {
    margin-top: 0;
  }

  .ant-select-dropdown-menu-item-disabled:hover,
  .ant-select-dropdown-menu-item-disabled {
    background-color: ${theme.colors.brandPrimaryContrast};
    color: rgba(0, 0, 0, 0.25);
    font-weight: normal;
  }

  .ant-select-dropdown-menu-item-partial-disabled:hover,
  .ant-select-dropdown-menu-item-partial-disabled {
    background-color: ${theme.colors.brandPrimaryContrast};
    color: rgba(0, 0, 0, 0.25);
    font-weight: normal;
  }

  .ant-select-dropdown-menu-item.highlighted-option {
    color: ${theme.colors.brandPrimary};
  }

  .ant-select-dropdown-menu-item.highlighted-option:hover,
  .ant-select-dropdown-menu-item-active.highlighted-option {
    background-color: ${theme.colors.brandPrimary};
    color: ${theme.colors.brandPrimaryContrast};
    font-weight: bold;
  }

  .ant-empty {
    text-align: center;
  }

  .ant-empty .ant-empty-image img {
    margin: 0 auto;
  }`}
`

const Button = styled(BerlitzButton)`
  ${({ theme }) => `
    padding: ${theme.space.xs} ${theme.space.md};
  `}
`

const H1 = styled(BerlitzH1)`
  ${({ theme }) => `
    font-size: ${theme.fontSize.xl};
    font-weight: ${theme.fontWeight.bold};
    font-stretch: normal;
    font-style: normal;
    line-height: 1.19;
    letter-spacing: 1.12px;
    text-align: center;
    margin-bottom: ${theme.space.md};
  `}
`

const Content = styled.div`
  text-align: center;
  max-width: 450px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  #attempts {
    color: ${({ theme }) => theme.colors.brandPrimary};
  }
`

const SelectWrapper = styled.div`
  min-width: 85%;
  max-width: 85%;
`

const SubLabel = styled.div`
  ${({ theme }) => `
    font-size: ${theme.fontSize.xxs};
    font-weight: ${theme.fontWeight.bold};
    line-height: 1.14;
    letter-spacing: 0.49px;
  `}
`

const SubLabel1 = styled.div`
  ${({ theme }) => `
    font-size: ${theme.fontSize.xs};
    font-weight: ${theme.fontWeight.bold};
    line-height: 1.14;
    letter-spacing: 0.49px;
  `}
`

const SubLabelWarning = styled.div`
  ${({ theme }) => `
    font-size: ${theme.fontSize.xs};
    color: ${theme.palette.danger70}; 
    font-weight: ${theme.fontWeight.bold};
    line-height: 1.14;
    letter-spacing: 0.49px;
  `}
`

const FullWidth = styled.div`
  width: 85%;
`

const Description = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  border: 1px solid #b4b4b4;
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.colors.brandSupporting01Contrast};
  color: ${({ theme }) => theme.palette.charcoal};
  min-height: 100px;
  max-height: 100px;
  overflow-y: auto;
  width: 100%;
  max-width: 100%;
  line-height: 1.5;
  white-space: pre-wrap;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.brandSupporting01Contrast};
  }
  &::-webkit-scrollbar-thumb {
    background: #b4b4b4;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
`

// ==================== COMPONENT ====================

const ModalSelectSkillProduct: React.FC<ModalSelectSkillProductProps> = ({
  id: cId,
  open,
  onClose = () => {},
  onButtonClick = () => {},
  title,
  buttonText,
  options = [],
  maxLimit,
  initialSelected = [],
  remainingSwitches,
  ...rest
}) => {
  const [openModal, setOpenModal] = useState(open)
  const [products, setProducts] = useState<ProductOption[]>([])
  const [skillsTutorRemainingOptions, setSkillsTutorRemainingOptions] = useState<ProductOption[]>(options)
  const [skillsTutorPlaceHolders, setSkillsTutorPlaceHolders] = useState<string[]>([])
  const [temporarySelectedSkillsTutor, setTemporarySelectedSkillsTutor] = useState<ProductOption | null>(null)
  const [isBtnDisable, setIsbtnDisable] = useState(true)
  const [id] = useState(cId || `${new Date().getTime()}-select-products-modal`)
  const intl = useIntl()

  const closeModal = () => {
    setOpenModal(false)
    onClose()
  }

  useEffect(() => {
    if (!initialSelected) return
    const selectedProducts: ProductOption[] = []
    options.forEach((option) => {
      if (initialSelected.includes(option.value)) {
        selectedProducts.push(option)
      }
    })
    setProducts(selectedProducts)
    setSkillsTutorRemainingOptions((skillsTutorRemainingOptions) =>
      skillsTutorRemainingOptions.filter((option) => !selectedProducts.some((prod) => prod.value === option.value))
    )
  }, [initialSelected, options])

  useEffect(() => {
    if (products.length === 2) {
      setSkillsTutorPlaceHolders([])
    } else if (products.length === 1) {
      setSkillsTutorPlaceHolders(['Skills Tutor 2'])
    } else {
      setSkillsTutorPlaceHolders(['Skills Tutor 1', 'Skills Tutor 2'])
    }
  }, [products])

  return (
    <Modal open={open || openModal} onClose={closeModal} size="sm" {...rest}>
      <WarningModal>
        <FormattedMessage id="Error" defaultMessage="Error" />
      </WarningModal>
      <StyledModalSelectSkillsTutor>
        <>
          <ImageWelcome width={'321'} height={'50'} />
          <Spacer size="sm" />
          <H1>{title}</H1>
          {remainingSwitches === 0 ? (
            <Content>
              <SubLabelWarning>
                <FormattedMessage
                  id="Complete one of your current skills tutors to select a new one."
                  defaultMessage="Complete one of your current skills tutors to select a new one."
                />
              </SubLabelWarning>
            </Content>
          ) : (
            <Content>
              {initialSelected && initialSelected.length === maxLimit ? (
                <SubLabel1>
                  <FormattedMessage
                    id="Remove an exisitng tutor to choose a new one."
                    defaultMessage="Remove an exisitng tutor to choose a new one."
                  />
                  <br />
                  <FormattedMessage
                    id="skills-tutor-selection-remaining-times"
                    defaultMessage="You may select a new skills tutor <attempts>{times}</attempts> {times, plural, one {more time} other {more times}}."
                    values={{
                      times: remainingSwitches,
                      attempts: (chunks) => <span id="attempts">{chunks}</span>,
                    }}
                  />
                </SubLabel1>
              ) : (
                <SubLabel1>
                  <FormattedMessage
                    id="skills-tutor-selected-count"
                    defaultMessage="Skills Tutors Selected: <attempts>{count}</attempts> of {maxLimit}."
                    values={{
                      count: products.length,
                      maxLimit,
                      attempts: (chunks) => <span id="attempts">{chunks}</span>,
                    }}
                  />
                </SubLabel1>
              )}
            </Content>
          )}
          <Spacer size="md" />
          {remainingSwitches !== 0 && (
            <>
              <SelectWrapper>
                <SubLabel>
                  <FormattedMessage id="Skills Tutor" defaultMessage="Skills Tutor" />
                </SubLabel>
                <Spacer size="xxs" />
                <StyledMultiSelect>
                  <Select
                    id={id}
                    options={skillsTutorRemainingOptions}
                    menuPlacement="top"
                    components={{ IndicatorSeparator: () => null }}
                    value={temporarySelectedSkillsTutor ? temporarySelectedSkillsTutor : null}
                    isDisabled={products.length === maxLimit}
                    placeholder={`${intl.formatMessage({ id: 'Select' })}....`}
                    styles={{
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: 'white',
                        color: state.isDisabled ? '#aaa' : '#1D1D1D',
                        cursor: 'pointer',
                        fontWeight: 500,
                        lineHeight: '1.4',
                        fontSize: 14,
                        ':hover': {
                          backgroundColor: '#004FEF',
                          color: '#FFF',
                        },
                      }),
                      menu: (provided) => ({
                        ...provided,
                        border: '1px solid #B4B4B4',
                        borderRadius: '2px',
                        boxShadow:
                          '0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%)',
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        borderRadius: 0,
                        minHeight: '36px',
                        backgroundColor: state.isDisabled ? '#F5F5F5' : 'white',
                      }),
                      container: (provided) => ({
                        ...provided,
                        borderRadius: 0,
                        minHeight: '36px',
                        width: '100%',
                        maxWidth: '100%',
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        padding: '4px 8px',
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        whiteSpace: 'normal',
                      }),
                    }}
                    onChange={(item) => {
                      setTemporarySelectedSkillsTutor(item as ProductOption)
                    }}
                  />
                </StyledMultiSelect>
              </SelectWrapper>
              <Spacer size="sm" />
              <SelectWrapper>
                <SubLabel>
                  <FormattedMessage id="Tutor Description" defaultMessage="Tutor Description" />
                </SubLabel>
                <Spacer size="xxs" />
                <Description
                  role="region"
                  aria-label={intl.formatMessage({ id: 'Tutor Description', defaultMessage: 'Tutor Description' })}
                  tabIndex={0}
                >
                  <div>
                    {temporarySelectedSkillsTutor?.slub
                      ? intl.formatMessage({
                          id: temporarySelectedSkillsTutor.slub,
                          defaultMessage: intl.formatMessage({
                            id: 'skills-tutor-description-placeholder',
                            defaultMessage: 'Select a tutor to view its description.',
                          }),
                        })
                      : intl.formatMessage({
                          id: 'skills-tutor-select-prompt',
                          defaultMessage: 'Select a tutor to view its description.',
                        })}
                  </div>
                </Description>
              </SelectWrapper>
              <Spacer size="sm" />
              <Button
                disabled={remainingSwitches === 0 || products.length === maxLimit || !temporarySelectedSkillsTutor}
                onClick={() => {
                  if (
                    temporarySelectedSkillsTutor &&
                    !products.find(({ value }) => value === temporarySelectedSkillsTutor.value)
                  ) {
                    const lessThanMaxLimit = isNumber(maxLimit) && products.length < maxLimit
                    if (maxLimit !== undefined && lessThanMaxLimit) {
                      setProducts([...products, temporarySelectedSkillsTutor])
                      setSkillsTutorRemainingOptions((skillsTutorRemainingOptions) =>
                        skillsTutorRemainingOptions.filter(
                          (option) => option.value !== temporarySelectedSkillsTutor.value
                        )
                      )
                      setIsbtnDisable(false)
                    }
                    if (maxLimit === undefined) {
                      setProducts([...products, temporarySelectedSkillsTutor])
                      setIsbtnDisable(false)
                    }
                  }
                  setTemporarySelectedSkillsTutor(null)
                }}
              >
                <FormattedMessage id="Select" defaultMessage="Select" />
              </Button>
              <Spacer size="xl" />
              <FullWidth>
                <Row>
                  {products.map((product) => (
                    <Col xs={12} md={12} key={`repeat-on-${product.key}`}>
                      <SelectedItem
                        name={product.label}
                        smallXButton
                        closeHidden={remainingSwitches === 0}
                        onClose={() => {
                          setProducts(products.filter(({ value }) => value !== product.value))
                          setIsbtnDisable(false)
                          const newOptions = [
                            ...skillsTutorRemainingOptions.slice(0, product?.index),
                            product,
                            ...skillsTutorRemainingOptions.slice(product?.index),
                          ]

                          setSkillsTutorRemainingOptions(newOptions)
                        }}
                      />
                    </Col>
                  ))}
                </Row>
                <Row>
                  {skillsTutorPlaceHolders.map((placeholder) => (
                    <Col xs={12} md={12} key={`repeat-on-${placeholder}`}>
                      <SelectedItem name={placeholder} closeHidden={true} />
                    </Col>
                  ))}
                </Row>
              </FullWidth>
              <Spacer size="sm" />
              <Button
                disabled={products.length === 0 || isBtnDisable || remainingSwitches === 0}
                onClick={() => onButtonClick(products)}
              >
                {buttonText}
              </Button>
              <Spacer size="sm" />
            </>
          )}
        </>
      </StyledModalSelectSkillsTutor>
    </Modal>
  )
}

export default ModalSelectSkillProduct
export type { ProductOption }
